import { useId, useMemo } from 'react';

import { DateTimeFormat } from '@xemplo/date-time';
import { InputErrorProps, InputSize, parseCssSizeVar } from '@xemplo/input-utils';
import { XemploInputProps } from '@xemplo/xemplo-input';

import { getFieldValues } from '../date-field.helper';
import { DateFieldProps } from '../date-field.types';
import { DateSelectorProps } from '../date-selector';

export function useDateFieldMeta(props: DateFieldProps) {
  const uid = useId();
  const {
    errorMessage,
    id,
    name,
    inputSize,
    width,
    placeholder,
    value,
    description,
    dateFormat = DateTimeFormat.Default,
    onValueChange,
    ...rest
  } = props;
  const inputId = `${uid}${id ?? name}`;
  const fieldWrapperId = `${inputId}-field-wrapper`;
  const xemploInputSize = inputSize ?? InputSize.Default;

  const fieldValues = useMemo(
    () => getFieldValues(value, dateFormat),
    [dateFormat, value],
  );

  const inputProps = useMemo(
    () =>
      ({
        ...rest,
        errorMessage,
        disableDescriptionOrError: true,
        id: inputId,
        inputSize: xemploInputSize,
        placeholder: placeholder ?? '',
        width: '100%',
        name,
        value: fieldValues.inputValue,
        disableErrorIcon: true,
      }) satisfies XemploInputProps,
    [rest, errorMessage, inputId, placeholder, xemploInputSize, name, fieldValues],
  );

  const maybeErrorOrDescriptionProps = useMemo(() => {
    const hasError = !!errorMessage;
    const errorId = hasError ? `${inputId}-error` : undefined;
    const descriptionId = description ? `${inputId}-description` : undefined;

    return {
      description,
      descriptionProps: descriptionId ? { id: descriptionId } : undefined,
      error: hasError,
      errorMessage,
      errorMessageProps: errorId ? { id: errorId } : undefined,
      testId: inputId,
    } satisfies InputErrorProps;
  }, [description, errorMessage, inputId]);

  const selectorProps = useMemo(() => {
    return {
      disabled: props.disabled,
      inputSize: xemploInputSize,
      readOnly: props.readOnly,
      onValueChange: onValueChange,
      defaultMonth: fieldValues.calendarValue ?? undefined,
      selected: fieldValues.calendarValue ?? undefined,
      onSelect: props.onSelect,
      onChange: props.onChange,
      dateFormat,
    } satisfies DateSelectorProps;
  }, [
    props.disabled,
    props.readOnly,
    props.onSelect,
    props.onChange,
    xemploInputSize,
    onValueChange,
    fieldValues.calendarValue,
    dateFormat,
  ]);

  return {
    fieldWrapperId,
    inputProps,
    inputSize: xemploInputSize,
    maybeErrorOrDescriptionProps,
    hasError: !!errorMessage,
    style: parseCssSizeVar('input-width', width),
    selectorProps,
  };
}
